import { Box, FormControl, Typography, Button, IconButton } from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { RoundedInputField } from '@/shared/ui/RoundedInputField'
import InputErrorPopUp from '@/pages/loginScreen/InputErrorPopUp'
import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MAX_PIN_LENGTH } from '@/shared/constants'

interface Props {
    saveNewPin: (oldPin: string, newPin: string) => Promise<boolean>
}

const PinSection = memo(({ saveNewPin }: Props) => {
    const [oldPin, setOldPin] = useState<string>('')
    const [newPin, setNewPin] = useState<string>('')
    const [confirmPin, setConfirmPin] = useState<string>('')
    const [showOldPin, setShowOldPin] = useState(false)
    const [showNewPin, setShowNewPin] = useState(false)
    const [showConfirmPin, setShowConfirmPin] = useState(false)
    const [errors, setErrors] = useState<{
        oldPin?: string | null
        newPin?: string | null
        confirmPin?: string | null
    }>({})
    const { t: translate } = useTranslation()

    const styles = {
        confirmButton: {
            marginTop: '25px',
            width: '100%',
            height: '36px',
            padding: '8px 12px',
            gap: '8px',
            borderRadius: '10px',
            bgcolor: 'primary.main',
        },
        inputField: {
            background: 'none',
            width: '100%',
            padding: '10px',
            borderRadius: '8px',
            border: '1px solid grey',
            color: 'white',
            marginTop: '5px',
            outline: 'none',
        },
    }

    const handleInputOldPinChange = (value: string) => {
        if (value.length <= MAX_PIN_LENGTH && /^\d*$/.test(value)) {
            setOldPin(value)
        }
    }

    const handleInputNewPinChange = (value: string) => {
        if (value.length <= MAX_PIN_LENGTH && /^\d*$/.test(value)) {
            setNewPin(value)
            setErrors((prev) => {
                const next = { ...prev }
                if (next.newPin) next.newPin = null
                if (next.confirmPin && value === confirmPin)
                    next.confirmPin = null
                return next
            })
        }
    }

    const handleInputConfirmPinChange = (value: string) => {
        if (value.length <= MAX_PIN_LENGTH && /^\d*$/.test(value)) {
            setConfirmPin(value)
            setErrors((prev) => {
                const next = { ...prev }
                if (next.confirmPin) next.confirmPin = null
                if (next.newPin && newPin === value) next.newPin = null
                return next
            })
        }
    }

    const handlePinSave = async () => {
        // Validate match first
        if (newPin !== confirmPin) {
            setErrors((prev) => ({
                ...prev,
                newPin: 'pinMismatch',
                confirmPin: 'pinMismatch',
            }))
            return
        }

        const status = await saveNewPin(oldPin, newPin)
        if (!status) {
            // Attribute backend failure to the old PIN by default
            setErrors((prev) => ({ ...prev, oldPin: 'invalidPin' }))
            return
        }

        // Success – reset all fields and errors
        setOldPin('')
        setNewPin('')
        setConfirmPin('')
        setErrors({})
    }

    return (
        <>
            <Box width="100%" sx={{ color: '#fff' }}>
                <Box
                    width="100%"
                    sx={{
                        color: '#fff',
                        marginTop: '20px',
                        marginBottom: '15px',
                    }}>
                    <Typography variant="label2" color="white">
                        {translate('settingsPageEditPinTitle')}
                    </Typography>
                </Box>
                <FormControl>
                    <Box sx={{ position: 'relative', mb: '14px' }}>
                        <RoundedInputField
                            native={{
                                type: showOldPin ? 'text' : 'password',
                                name: 'oldPin',
                                minLength: MAX_PIN_LENGTH,
                                maxLength: MAX_PIN_LENGTH,
                                pattern: '[0-9]',
                                spellCheck: false,
                                inputMode: 'numeric',
                            }}
                            sx={styles.inputField}
                            placeholder={translate(
                                'settingsPageInputOldPinTitle'
                            )}
                            searchValue={oldPin}
                            setSearchValue={handleInputOldPinChange}
                        />
                        <IconButton
                            aria-label="toggle old pin visibility"
                            onClick={() => setShowOldPin((prev) => !prev)}
                            edge="end"
                            sx={{
                                position: 'absolute',
                                right: '10px',
                                top: '58%',
                                transform: 'translateY(-50%)',
                                color: oldPin ? 'white' : 'grey',
                            }}>
                            {showOldPin ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        {!!errors.oldPin && (
                            <InputErrorPopUp
                                styles={{
                                    position: 'absolute',
                                    top: '46px',
                                    left: '15px',
                                    width: 'max-content',
                                }}
                                errorMessage={translate(errors.oldPin)}
                            />
                        )}
                    </Box>

                    {/* subtle separator between old PIN and new PIN section */}
                    <Box sx={{ height: '8px' }} />

                    <Box sx={{ position: 'relative' }}>
                        <RoundedInputField
                            native={{
                                type: showNewPin ? 'text' : 'password',
                                name: 'newPin',
                                minLength: MAX_PIN_LENGTH,
                                maxLength: MAX_PIN_LENGTH,
                                pattern: '[0-9]',
                                spellCheck: false,
                                inputMode: 'numeric',
                            }}
                            sx={styles.inputField}
                            placeholder={translate(
                                'settingsPageInputNewPinTitle'
                            )}
                            searchValue={newPin}
                            setSearchValue={handleInputNewPinChange}
                        />
                        <IconButton
                            aria-label="toggle new pin visibility"
                            onClick={() => setShowNewPin((prev) => !prev)}
                            edge="end"
                            sx={{
                                position: 'absolute',
                                right: '10px',
                                top: '58%',
                                transform: 'translateY(-50%)',
                                color: newPin ? 'white' : 'grey',
                            }}>
                            {showNewPin ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        {!!errors.newPin && (
                            <InputErrorPopUp
                                styles={{
                                    position: 'absolute',
                                    top: '46px',
                                    left: '15px',
                                    width: 'max-content',
                                }}
                                errorMessage={translate(errors.newPin)}
                            />
                        )}
                    </Box>

                    <Box sx={{ position: 'relative' }}>
                        <RoundedInputField
                            native={{
                                type: showConfirmPin ? 'text' : 'password',
                                name: 'confirmPin',
                                minLength: MAX_PIN_LENGTH,
                                maxLength: MAX_PIN_LENGTH,
                                pattern: '[0-9]',
                                spellCheck: false,
                                inputMode: 'numeric',
                            }}
                            sx={styles.inputField}
                            placeholder={translate(
                                'settingsPageInputConfirmPinTitle'
                            )}
                            searchValue={confirmPin}
                            setSearchValue={handleInputConfirmPinChange}
                        />
                        <IconButton
                            aria-label="toggle confirm pin visibility"
                            onClick={() => setShowConfirmPin((prev) => !prev)}
                            edge="end"
                            sx={{
                                position: 'absolute',
                                right: '10px',
                                top: '58%',
                                transform: 'translateY(-50%)',
                                color: confirmPin ? 'white' : 'grey',
                            }}>
                            {showConfirmPin ? (
                                <VisibilityOff />
                            ) : (
                                <Visibility />
                            )}
                        </IconButton>
                        {!!errors.confirmPin && (
                            <InputErrorPopUp
                                styles={{
                                    position: 'absolute',
                                    top: '46px',
                                    left: '15px',
                                    width: 'max-content',
                                }}
                                errorMessage={translate(errors.confirmPin)}
                            />
                        )}
                    </Box>

                    <Button
                        data-testid="accept-button"
                        sx={styles.confirmButton}
                        onClick={handlePinSave}>
                        <Typography color="white" variant="label4">
                            {translate('accept')}
                        </Typography>
                    </Button>
                </FormControl>
            </Box>
        </>
    )
})

PinSection.displayName = 'PinSection'

export { PinSection }
